<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useSectionPermissions } from 'dashboard/composables/useSectionPermissions';

const { t } = useI18n();

const { canEditSection } = useSectionPermissions();

const props = defineProps({
  source: {
    type: Object,
    required: true,
  },
  statusColor: {
    type: String,
    default: '',
  },
  statusLabel: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['process', 'reprocess', 'delete', 'preview', 'sync', 'edit']);

const expanded = ref(false);
const previewData = ref(null);
const loadingPreview = ref(false);

const isApiSource = computed(() => props.source.source_type === 'api');

const sourceIcon = computed(() => {
  if (isApiSource.value) return 'i-lucide-globe text-indigo-500';
  switch (props.source.file_type) {
    case 'pdf':  return 'i-lucide-file-text text-red-500';
    case 'xlsx':
    case 'xls':  return 'i-lucide-file-spreadsheet text-emerald-500';
    case 'csv':  return 'i-lucide-file-spreadsheet text-blue-500';
    case 'txt':  return 'i-lucide-file text-slate-500';
    default:     return 'i-lucide-file text-slate-500';
  }
});

const typeBadge = computed(() => {
  if (isApiSource.value) {
    return {
      type: t('OMNI.DATA_SOURCES.API_SOURCE.BADGE'),
      color: 'bg-indigo-50 text-indigo-600 dark:bg-indigo-900/20 dark:text-indigo-400',
    };
  }
  const type = props.source.file_type?.toUpperCase() || 'FILE';
  const colors = {
    PDF:  'bg-red-50 text-red-600 dark:bg-red-900/20 dark:text-red-400',
    XLSX: 'bg-emerald-50 text-emerald-600 dark:bg-emerald-900/20 dark:text-emerald-400',
    XLS:  'bg-emerald-50 text-emerald-600 dark:bg-emerald-900/20 dark:text-emerald-400',
    CSV:  'bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400',
    TXT:  'bg-slate-50 text-slate-600 dark:bg-slate-700 dark:text-slate-400',
  };
  return { type, color: colors[type] || colors.TXT };
});

const displayName = computed(() => {
  if (isApiSource.value) {
    try {
      const url = new URL(props.source.api_url || '');
      return url.hostname + url.pathname;
    } catch {
      return props.source.api_url || 'API Source';
    }
  }
  return props.source.file_name || 'Unnamed file';
});

const formattedSize = computed(() => {
  if (isApiSource.value) return null;
  const bytes = props.source.file_size || 0;
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
});

const formattedDate = computed(() => {
  return new Date(props.source.created_at).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
});

const lastSyncedFormatted = computed(() => {
  if (!props.source.last_synced_at) return t('OMNI.DATA_SOURCES.API_SOURCE.NEVER_SYNCED');
  return new Date(props.source.last_synced_at).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
});

const syncIntervalLabel = computed(() => {
  const mins = props.source.sync_interval_minutes;
  if (!props.source.sync_enabled || !mins) return t('OMNI.DATA_SOURCES.API_SOURCE.SYNC_DISABLED');
  if (mins < 60) return `${mins}min`;
  if (mins < 1440) return `${mins / 60}h`;
  return `${mins / 1440}d`;
});

const canReprocess = computed(
  () =>
    (props.source.status === 'completed' || props.source.status === 'failed') &&
    (isApiSource.value || props.source.file_available !== false)
);
const isProcessing = computed(() => props.source.status === 'processing');
const isPending    = computed(() => props.source.status === 'pending');
const isCompleted  = computed(() => props.source.status === 'completed');
const isFailed     = computed(() => props.source.status === 'failed');

const timelineSteps = computed(() => {
  if (isApiSource.value) {
    return [
      { label: 'Created', done: true, active: false, icon: 'i-lucide-globe' },
      {
        label: 'Sync',
        done: isCompleted.value || isFailed.value,
        active: isProcessing.value,
        icon: isProcessing.value ? 'i-lucide-loader-2 animate-spin' : 'i-lucide-refresh-cw',
      },
      {
        label: 'Done',
        done: isCompleted.value,
        active: false,
        failed: isFailed.value,
        icon: isFailed.value ? 'i-lucide-x' : 'i-lucide-check',
      },
    ];
  }

  return [
    { label: 'Upload', done: true, active: false, icon: 'i-lucide-upload' },
    {
      label: 'Processing',
      done: isCompleted.value || isFailed.value,
      active: isProcessing.value,
      icon: isProcessing.value ? 'i-lucide-loader-2 animate-spin' : 'i-lucide-cpu',
    },
    {
      label: 'Done',
      done: isCompleted.value,
      active: false,
      failed: isFailed.value,
      icon: isFailed.value ? 'i-lucide-x' : 'i-lucide-check',
    },
  ];
});

const handleToggleExpand = async () => {
  expanded.value = !expanded.value;
  if (expanded.value && !previewData.value && !isApiSource.value && props.source.file_available !== false) {
    loadingPreview.value = true;
    try {
      const result = await emit('preview', props.source.id);
      if (result) previewData.value = result;
    } catch {
      // Preview not available
    } finally {
      loadingPreview.value = false;
    }
  }
};
</script>

<template>
  <div
    class="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:border-woot-500 dark:hover:border-woot-500 transition-colors overflow-hidden"
  >
    <!-- Card Header -->
    <div class="p-4">
      <div class="flex items-start justify-between mb-3">
        <div class="flex items-center gap-3 min-w-0 flex-1">
          <div
            class="flex h-10 w-10 items-center justify-center rounded-lg flex-shrink-0"
            :class="isApiSource ? 'bg-indigo-50 dark:bg-indigo-900/20' : 'bg-slate-50 dark:bg-slate-800'"
          >
            <span :class="sourceIcon" class="text-xl" />
          </div>
          <div class="min-w-0 flex-1">
            <h3
              class="font-medium text-slate-900 dark:text-slate-100 truncate text-sm"
              :title="displayName"
            >
              {{ displayName }}
            </h3>
            <div class="flex items-center gap-2 mt-0.5">
              <span
                class="px-1.5 py-0.5 text-[10px] font-semibold rounded"
                :class="typeBadge.color"
              >
                {{ typeBadge.type }}
              </span>
              <span v-if="formattedSize" class="text-xs text-slate-400">{{ formattedSize }}</span>
              <span v-if="isApiSource && source.api_method" class="text-xs text-slate-400 font-mono">{{ source.api_method }}</span>
            </div>
          </div>
        </div>
        <span
          class="px-2 py-1 text-xs font-medium rounded-lg whitespace-nowrap ml-2"
          :class="statusColor"
        >
          {{ statusLabel }}
        </span>
      </div>

      <!-- Timeline -->
      <div class="flex items-center gap-0 mb-3">
        <template v-for="(step, index) in timelineSteps" :key="step.label">
          <div class="flex items-center gap-1.5">
            <div
              class="flex h-6 w-6 items-center justify-center rounded-full text-xs transition-all"
              :class="[
                step.done && !step.failed
                  ? 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400'
                  : step.active
                    ? 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400'
                    : step.failed
                      ? 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400'
                      : 'bg-slate-100 text-slate-400 dark:bg-slate-800 dark:text-slate-500',
              ]"
            >
              <span :class="step.icon" class="w-3 h-3" />
            </div>
            <span
              class="text-[10px] font-medium"
              :class="step.done || step.active ? 'text-slate-700 dark:text-slate-300' : 'text-slate-400 dark:text-slate-500'"
            >
              {{ step.label }}
            </span>
          </div>
          <div
            v-if="index < timelineSteps.length - 1"
            class="flex-1 h-px mx-1.5"
            :class="step.done ? 'bg-emerald-200 dark:bg-emerald-800' : step.active ? 'bg-blue-200 dark:bg-blue-800' : 'bg-slate-200 dark:bg-slate-700'"
          />
        </template>
      </div>

      <!-- Info Row -->
      <div class="flex items-center gap-4 text-xs text-slate-500 dark:text-slate-400 flex-wrap">
        <div v-if="source.collection" class="flex items-center gap-1">
          <span class="i-lucide-folder" />
          <span>{{ source.collection }}</span>
        </div>
        <div class="flex items-center gap-1">
          <span class="i-lucide-database" />
          <span>{{ source.entries_count }} {{ t('OMNI.DATA_SOURCES.ENTRIES') }}</span>
        </div>
        <div class="flex items-center gap-1">
          <span class="i-lucide-calendar" />
          <span>{{ formattedDate }}</span>
        </div>
        <!-- API-specific: last synced -->
        <div v-if="isApiSource" class="flex items-center gap-1">
          <span class="i-lucide-refresh-cw" />
          <span>{{ lastSyncedFormatted }}</span>
        </div>
        <!-- API-specific: sync interval badge -->
        <div v-if="isApiSource && source.sync_enabled" class="flex items-center gap-1 text-indigo-600 dark:text-indigo-400">
          <span class="i-lucide-clock" />
          <span>{{ syncIntervalLabel }}</span>
        </div>
      </div>

      <!-- Error Message -->
      <div
        v-if="isFailed && source.error_message"
        class="mt-3 p-2.5 bg-red-50 dark:bg-red-900/10 rounded-lg text-xs text-red-600 dark:text-red-400 border border-red-100 dark:border-red-900/20"
      >
        <div class="flex items-start gap-2">
          <span class="i-lucide-alert-circle flex-shrink-0 mt-0.5" />
          <span>{{ source.error_message }}</span>
        </div>
      </div>
    </div>

    <!-- Actions -->
    <div
      class="flex items-center justify-between px-4 py-2.5 bg-slate-50/50 dark:bg-slate-800/30 border-t border-slate-100 dark:border-slate-800"
    >
      <div class="flex gap-2">
        <!-- File source pending -->
        <button
          v-if="isPending && !isApiSource"
          class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-woot-500 rounded-md hover:bg-woot-600 transition-colors"
          @click="emit('process', source.id)"
        >
          <span class="i-lucide-play w-3 h-3" />
          {{ t('OMNI.DATA_SOURCES.PROCESS') }}
        </button>

        <!-- Reprocess (files) -->
        <button
          v-if="canReprocess && !isApiSource"
          class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-700 rounded-md hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors"
          @click="emit('reprocess', source.id)"
        >
          <span class="i-lucide-refresh-cw w-3 h-3" />
          {{ t('OMNI.DATA_SOURCES.REPROCESS') }}
        </button>

        <!-- API source: Sync Now -->
        <button
          v-if="isApiSource && !isProcessing"
          class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-woot-600 bg-woot-50 dark:bg-woot-900/20 rounded-md hover:bg-woot-100 dark:hover:bg-woot-900/30 transition-colors"
          @click="emit('sync', source.id)"
        >
          <span class="i-lucide-refresh-cw w-3 h-3" />
          {{ t('OMNI.DATA_SOURCES.API_SOURCE.SYNC_NOW') }}
        </button>

        <div
          v-if="isProcessing"
          class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs text-blue-600 dark:text-blue-400"
        >
          <span class="i-lucide-loader-2 animate-spin w-3 h-3" />
          {{ isApiSource ? t('OMNI.DATA_SOURCES.API_SOURCE.SYNCING') : t('OMNI.DATA_SOURCES.PROCESSING') }}
        </div>
      </div>

      <div v-if="canEditSection('data_sources')" class="flex items-center gap-1">
        <!-- Edit (API sources only) -->
        <button
          v-if="isApiSource"
          class="p-1.5 text-woot-500 hover:bg-woot-50 dark:hover:bg-woot-900/20 rounded"
          :title="t('OMNI.DATA_SOURCES.API_SOURCE.EDIT_BUTTON')"
          @click="emit('edit', source)"
        >
          <span class="i-lucide-edit w-4 h-4" />
        </button>

        <button
          class="p-1.5 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
          :title="t('OMNI.DATA_SOURCES.DELETE')"
          @click="emit('delete', source.id)"
        >
          <span class="i-lucide-trash-2 w-4 h-4" />
        </button>
      </div>
    </div>
  </div>
</template>
